import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Waves, Gauge, Timer, Weight, CheckCircle2, AlertCircle,
  Info, Save, RotateCcw, Microscope, BarChart3, Zap,
  Thermometer, ArrowDown, ArrowRightLeft, Volume2,
  Factory, Milk, FishSymbol, ShieldAlert, Droplets,
  FlaskConical, Settings, Beaker, ArrowRight, Filter,
  Database
} from "lucide-react";
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

interface EQParams {
  flowRate: number;       // m³/day
  peakFactor: number;     // × average
  minFlow: number;        // m³/day
  influentBOD: number;    // mg/L
  influentCOD: number;    // mg/L
  influentTSS: number;    // mg/L
  influentpH: number;     // pH
  temperature: number;    // °C
  og: number;
  ph: number;
  alkalinity: number;
  turbidity: number;
  conductivity: number;
  hardness: number;
  nh3n: number;
  tp: number;
  tankLength: number;     // m
  tankWidth: number;      // m
  tankDepth: number;      // m
  freeboard: number;      // m
  workingVolumePercent: number; // %
  mixerType: 'none' | 'jet' | 'mechanical' | 'diffused';
  mixerPower: number;     // kW
  retentionTime: number;  // h target
}

interface EQSize {
  totalVolume: number;
  workingVolume: number;
  freeboardVolume: number;
  tankArea: number;
  hlr: number;
  peakHLR: number;
  avgDetention: number;
  peakDetention: number;
  minDetention: number;
  volumeTurnover: number;
  peakShaveVolume: number;
  peakShavePercent: number;
  volumeVariance: number;
  codLoad: number;       // kg/day
  bodLoad: number;       // kg/day
  tssLoad: number;       // kg/day
  gValue: number;        // s⁻¹
  mixingTime: number;    // min
  turnoverTime: number;  // h
  stratificationRisk: string;
  shortCircuitRisk: string;
  odourRisk: string;
  status: 'optimal' | 'acceptable' | 'undersized' | 'oversized' | 'overloaded';
  statusDetail: string;
  designNotes: string[];
}

const PROFILES = [
  { name: 'Dairy Effluent', description: 'High diurnal variation, moderate organics', icon: Milk,
    defaults: { flowRate: 250, peakFactor: 2.5, minFlow: 80, influentBOD: 1850, influentCOD: 3200, influentTSS: 450, influentpH: 6.8, temperature: 25,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      tankLength: 8, tankWidth: 4, tankDepth: 3.5, freeboard: 0.5, workingVolumePercent: 90,
      mixerType: 'mechanical' as const, mixerPower: 2.5, retentionTime: 12 } },
  { name: 'Brewery Effluent', description: 'Very high variation, strong pH swings', icon: Factory,
    defaults: { flowRate: 350, peakFactor: 3.0, minFlow: 100, influentBOD: 2200, influentCOD: 4100, influentTSS: 620, influentpH: 5.2, temperature: 20,
      og: 180, ph: 5.2, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      tankLength: 12, tankWidth: 6, tankDepth: 4, freeboard: 0.5, workingVolumePercent: 90,
      mixerType: 'jet' as const, mixerPower: 4, retentionTime: 16 } },
  { name: 'Fish Processing', description: 'Moderate flow, steady but high solids', icon: FishSymbol,
    defaults: { flowRate: 150, peakFactor: 2.0, minFlow: 60, influentBOD: 1450, influentCOD: 2650, influentTSS: 520, influentpH: 6.9, temperature: 15,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      tankLength: 6, tankWidth: 3, tankDepth: 3, freeboard: 0.5, workingVolumePercent: 90,
      mixerType: 'mechanical' as const, mixerPower: 1.5, retentionTime: 8 } },
];

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

export function calcEQ(p: EQParams): EQSize {
  const Q = p.flowRate; // m³/day
  const q = Q / 24; // m³/h average
  const peakQ = q * p.peakFactor; // m³/h peak
  const minQ = p.minFlow / 24; // m³/h minimum

  // Geometry
  const tankArea = p.tankLength * p.tankWidth;
  const totalVolume = tankArea * (p.tankDepth + p.freeboard);
  const workingVolume = tankArea * p.tankDepth * (p.workingVolumePercent / 100);
  const freeboardVolume = totalVolume - workingVolume;

  // Hydraulics
  const avgDetention = workingVolume / q; // h
  const peakDetention = workingVolume / peakQ; // h at peak
  const minDetention = workingVolume / minQ; // h at minimum
  const hlr = q / tankArea; // m³/m²·h
  const peakHLR = peakQ / tankArea;
  const volumeTurnover = Q / workingVolume; // ×/day

  // Peak shaving
  const peakDuration = 2; // h assumed
  const peakShaveVolume = (peakQ - q) * peakDuration; // m³
  const peakShavePercent = (peakShaveVolume / workingVolume) * 100;
  const volumeVariance = peakShavePercent;

  // Mass balance / loading
  const codLoad = (Q * p.influentCOD) / 1000; // kg/day
  const bodLoad = (Q * p.influentBOD) / 1000; // kg/day
  const tssLoad = (Q * p.influentTSS) / 1000; // kg/day

  // Mixing physics
  const tempK = p.temperature + 273.15;
  const mu = 0.001 * Math.exp(-1.5 * (p.temperature - 20) / tempK);
  const mixerPowerW = p.mixerPower * 1000;
  const gValue = p.mixerType !== 'none' && p.mixerPower > 0
    ? Math.sqrt(mixerPowerW / (mu * workingVolume)) : 0; // s⁻¹
  const mixingTime = q > 0 ? (workingVolume / q) * 60 : 0; // min turnover
  const turnoverTime = workingVolume / q; // h

  // Stratification risk
  const stratificationRisk = p.mixerType === 'none' ? 'high'
    : gValue < 15 ? 'moderate'
    : 'low';
  const shortCircuitRisk = minDetention < 0.5 ? 'high'
    : peakDetention < 1 ? 'moderate'
    : 'low';
  const odourRisk = avgDetention > 48 ? 'high'
    : avgDetention > 24 ? 'moderate'
    : 'low';

  // Status
  let status: EQSize['status']; let statusDetail: string;
  const designNotes: string[] = [];

  if (avgDetention >= 8 && peakDetention >= 2 && volumeTurnover <= 6 && p.mixerType !== 'none') {
    status = 'optimal';
    statusDetail = `Optimal. Detention ${avgDetention.toFixed(1)} h, turnover ${volumeTurnover.toFixed(1)}×/d, G ${gValue.toFixed(0)} s⁻¹.`;
  } else if (avgDetention >= 4 && peakDetention >= 1 && volumeTurnover <= 10) {
    status = 'acceptable';
    statusDetail = `Acceptable. Detention ${avgDetention.toFixed(1)} h. Monitor mixing.`;
  } else if (avgDetention < 1 || volumeTurnover > 15 || workingVolume < 10) {
    status = 'overloaded';
    statusDetail = `Overloaded. Detention ${avgDetention.toFixed(1)} h too short. Increase volume.`;
  } else if (workingVolume < 20) {
    status = 'undersized';
    statusDetail = `Undersized. Working volume ${workingVolume.toFixed(1)} m³. Increase tank size.`;
  } else {
    status = 'oversized';
    statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  if (p.mixerType === 'none') designNotes.push('⚠️ No mixing — stratification risk. Install mixer.');
  if (avgDetention > 48) designNotes.push('⚠️ Long detention >48h — odour and septicity risk.');
  if (peakShavePercent > 50) designNotes.push('⚠️ Peak shaving volume >50% of working volume.');
  if (p.influentpH < 5.5) designNotes.push('⚠️ Low influent pH — corrosion risk in tank.');
  if (designNotes.length === 0) designNotes.push('✅ All design parameters within recommended ranges.');

  return {
    totalVolume, workingVolume, freeboardVolume, tankArea,
    hlr, peakHLR, avgDetention, peakDetention, minDetention,
    volumeTurnover, peakShaveVolume, peakShavePercent, volumeVariance,
    codLoad, bodLoad, tssLoad,
    gValue, mixingTime, turnoverTime,
    stratificationRisk, shortCircuitRisk, odourRisk,
    status, statusDetail, designNotes,
  };
}

/* ─── UI Components ──────────────────────────────────── */
function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && <div className="group relative"><Info className="w-3 h-3 text-slate-600 cursor-help" /><div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">{info}</div></div>}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">{step < 0.1 ? safeValue.toFixed(2) : step < 1 ? safeValue.toFixed(1) : safeValue.toFixed(0)}<span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span></span>
      </div>
      <input type="range" min={min} max={max} step={step} value={safeValue} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer" style={{ accentColor: color }} />
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  const safeMax = typeof max === 'number' && max > 0 ? max : 1;
  const pct = Math.min(100, (safeValue / safeMax) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]"><span className="text-slate-500">{label}</span><span className="font-bold" style={{ color }}>{fmt(safeValue, 1)} {unit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} /></div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button onClick={onClick} className={`w-full text-left p-3 rounded-xl border transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-500/20' : 'bg-slate-800'}`}><Icon className={`w-4 h-4 ${isActive ? 'text-amber-400' : 'text-slate-500'}`} /></div>
        <div><p className={`text-xs font-semibold ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p><p className="text-[10px] text-slate-500 leading-tight">{profile.description}</p></div>
      </div>
    </button>
  );
}

/* ─── Flow Diagram ───────────────────────────────────── */
function FlowDiagram({ params, size }: { params: EQParams; size: EQSize }) {
  const safe = {
    Q: typeof params.flowRate === 'number' ? params.flowRate / 24 : 0,
    avgDet: typeof size?.avgDetention === 'number' ? size.avgDetention : 0,
    peakDet: typeof size?.peakDetention === 'number' ? size.peakDetention : 0,
    volVar: typeof size?.volumeVariance === 'number' ? size.volumeVariance : 0,
    gVal: typeof size?.gValue === 'number' ? size.gValue : 0,
    codLoad: typeof size?.codLoad === 'number' ? size.codLoad : 0,
    bodLoad: typeof size?.bodLoad === 'number' ? size.bodLoad : 0,
    tssLoad: typeof size?.tssLoad === 'number' ? size.tssLoad : 0,
  };

  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowRightLeft className="w-3 h-3" /> Process Flow — Influent → Equalization → Effluent
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs>
          <linearGradient id="waterGradE" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">EQUALIZATION TANK — FLOW DAMPING & MIXING</text>

        {/* Influent with peak */}
        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradE)" stroke="#f59e0b" strokeWidth="2" />
        <text x="80" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Influent</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Q avg {fmt(params.flowRate, 0)} m³/d</text>
        <text x="80" y="100" fill="#f59e0b" fontSize="8" textAnchor="middle">Q peak {(params.flowRate * params.peakFactor).toFixed(0)} m³/d</text>
        <text x="80" y="112" fill="#f59e0b" fontSize="8" textAnchor="middle">BOD {fmt(params.influentBOD, 0)} mg/L</text>
        <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">COD {fmt(params.influentCOD, 0)} mg/L</text>

        <line x1="132" y1="95" x2="170" y2="95" stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="168,92 175,95 168,98" fill="#f59e0b" />

        {/* EQ Tank */}
        <rect x="180" y="45" width="300" height="100" rx="6" fill="url(#waterGradE)" stroke="#06b6d4" strokeWidth="2" />
        <text x="330" y="62" fill="#06b6d4" fontSize="10" textAnchor="middle" fontWeight="bold">Equalization Tank</text>
        <text x="330" y="78" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.tankLength, 1)} × {fmt(params.tankWidth, 1)} × {fmt(params.tankDepth, 1)} m | Vol {fmt(size.workingVolume, 0)} m³</text>
        <text x="330" y="92" fill="#94a3b8" fontSize="8" textAnchor="middle">Avg detention {fmt(safe.avgDet, 1)} h | Peak {fmt(safe.peakDet, 1)} h</text>
        <text x="330" y="106" fill="#f59e0b" fontSize="8" textAnchor="middle">Peak shave {fmt(safe.volVar, 1)}% | Turnover {fmt(size.volumeTurnover, 1)}×/d</text>
        <text x="330" y="120" fill="#94a3b8" fontSize="8" textAnchor="middle">Mixer: {params.mixerType} | G = {fmt(safe.gVal, 0)} s⁻¹</text>

        {/* Mixer impeller */}
        {params.mixerType !== 'none' && (
          <motion.g animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2.5, ease: "linear" }} style={{ transformOrigin: '230px 100px' }}>
            <circle cx="230" cy="100" r="10" fill="none" stroke="#06b6d4" strokeWidth="1.5" />
            <line x1="230" y1="90" x2="230" y2="110" stroke="#06b6d4" strokeWidth="1.5" />
            <line x1="220" y1="100" x2="240" y2="100" stroke="#06b6d4" strokeWidth="1.5" />
          </motion.g>
        )}

        <line x1="482" y1="95" x2="520" y2="95" stroke="#06b6d4" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="518,92 525,95 518,98" fill="#06b6d4" />

        {/* Effluent */}
        <rect x="530" y="55" width="100" height="80" rx="6" fill="url(#waterGradE)" stroke="#10b981" strokeWidth="2" />
        <text x="580" y="72" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Effluent</text>
        <text x="580" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Damped flow to process</text>
        <text x="580" y="100" fill="#10b981" fontSize="8" textAnchor="middle">BOD load {fmt(safe.bodLoad, 1)} kg/d</text>
        <text x="580" y="112" fill="#10b981" fontSize="8" textAnchor="middle">COD load {fmt(safe.codLoad, 1)} kg/d</text>
        <text x="580" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">TSS load {fmt(safe.tssLoad, 1)} kg/d</text>

        {/* Loadings panel */}
        <rect x="660" y="55" width="200" height="120" rx="6" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="760" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Mass Loading Balance</text>
        <text x="760" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">COD: {fmt(safe.codLoad, 1)} kg/day</text>
        <text x="760" y="100" fill="#94a3b8" fontSize="8" textAnchor="middle">BOD: {fmt(safe.bodLoad, 1)} kg/day</text>
        <text x="760" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">TSS: {fmt(safe.tssLoad, 1)} kg/day</text>
        <text x="760" y="128" fill="#f59e0b" fontSize="8" textAnchor="middle">pH: {fmt(params.influentpH, 1)} | Temp: {fmt(params.temperature, 0)}°C</text>
        <text x="760" y="140" fill="#94a3b8" fontSize="8" textAnchor="middle">Risk: {size.stratificationRisk} stratification</text>
        <text x="760" y="152" fill="#94a3b8" fontSize="8" textAnchor="middle">Risk: {size.odourRisk} odour potential</text>

        {/* Legend */}
        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#f59e0b" fontSize="9">— Influent (variable)</text>
        <text x="155" y="359" fill="#06b6d4" fontSize="9">— Equalization + Mixing</text>
        <text x="295" y="359" fill="#10b981" fontSize="9">— Effluent (damped)</text>
        <text x="420" y="359" fill="#94a3b8" fontSize="9">Det={fmt(safe.avgDet, 1)}h | Turnover={fmt(size.volumeTurnover, 1)}× | G={fmt(safe.gVal, 0)}s⁻¹</text>
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
export default function EQSpecPage() {
  const [params, setParams] = useState<EQParams>(PROFILES[0].defaults);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'influent' | 'tank' | 'mixing'>('influent');
  const [profileIndex, setProfileIndex] = useState(0);

  const size = useMemo(() => calcEQ(params), [params]);
  const sampleLoader = useSampleLoader<EQParams>(setParams);

  const handleReset = () => { setParams(PROFILES[0].defaults); setProfileIndex(0); sampleLoader.clearSample(); toast.info('Parameters reset to defaults'); };
  const handleSave = () => { localStorage.setItem('eq-params-v2', JSON.stringify(params)); setSaved(true); setTimeout(() => setSaved(false), 2000); toast.success('Configuration saved'); };

  useEffect(() => {
    const savedParams = localStorage.getItem('eq-params-v2');
    if (savedParams) { try { setParams(JSON.parse(savedParams)); } catch {} }
  }, []);

  const scienceSections = useMemo(() => {
    const Q = params.flowRate;
    const q = Q / 24;
    const peakQ = q * params.peakFactor;
    const minQ = params.minFlow / 24;
    const tankArea = params.tankLength * params.tankWidth;
    const totalVolume = tankArea * (params.tankDepth + params.freeboard);
    const workingVolume = tankArea * params.tankDepth * (params.workingVolumePercent / 100);
    const avgDetention = workingVolume / q;
    const peakDetention = workingVolume / peakQ;
    const minDetention = workingVolume / minQ;
    const hlr = q / tankArea;
    const peakHLR = peakQ / tankArea;
    const volumeTurnover = Q / workingVolume;
    const peakDuration = 2;
    const peakShaveVolume = (peakQ - q) * peakDuration;
    const peakShavePercent = (peakShaveVolume / workingVolume) * 100;
    const codLoad = (Q * params.influentCOD) / 1000;
    const bodLoad = (Q * params.influentBOD) / 1000;
    const tssLoad = (Q * params.influentTSS) / 1000;
    const tempK = params.temperature + 273.15;
    const mu = 0.001 * Math.exp(-1.5 * (params.temperature - 20) / tempK);
    const mixerPowerW = params.mixerPower * 1000;
    const gValue = params.mixerType !== 'none' && params.mixerPower > 0
      ? Math.sqrt(mixerPowerW / (mu * workingVolume)) : 0;
    const mixingTime = q > 0 ? (workingVolume / q) * 60 : 0;
    const turnoverTime = workingVolume / q;
    return [
      {
        title: 'Hydraulics — Detention Time & Peak Shaving',
        icon: Waves,
        equations: [
          { label: 'Average flow', formula: 'q = Q / 24', substitution: `q = ${sharedFmt(Q, 0)} / 24 = ${sharedFmt(q, 1)} m³/h`, result: sharedFmt(q, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Peak flow', formula: 'q_peak = q × peakFactor', substitution: `q_peak = ${sharedFmt(q, 1)} × ${params.peakFactor} = ${sharedFmt(peakQ, 1)} m³/h`, result: sharedFmt(peakQ, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Tank area', formula: 'A = L × W', substitution: `A = ${params.tankLength} × ${params.tankWidth} = ${sharedFmt(tankArea, 1)} m²`, result: sharedFmt(tankArea, 1), status: 'pass', unit: 'm²' },
          { label: 'Working volume', formula: 'V_w = A × depth × (working%/100)', substitution: `V_w = ${sharedFmt(tankArea, 1)} × ${params.tankDepth} × ${params.workingVolumePercent / 100} = ${sharedFmt(workingVolume, 1)} m³`, result: sharedFmt(workingVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Avg detention', formula: 't = V_w / q', substitution: `t = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(q, 1)} = ${sharedFmt(avgDetention, 1)} h`, result: sharedFmt(avgDetention, 1), status: avgDetention >= 4 ? 'pass' : 'warn', unit: 'h' },
          { label: 'Peak detention', formula: 't_peak = V_w / q_peak', substitution: `t_peak = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(peakQ, 1)} = ${sharedFmt(peakDetention, 1)} h`, result: sharedFmt(peakDetention, 1), status: peakDetention >= 1 ? 'pass' : 'warn', unit: 'h' },
          { label: 'HLR', formula: 'HLR = q / A', substitution: `HLR = ${sharedFmt(q, 1)} / ${sharedFmt(tankArea, 1)} = ${sharedFmt(hlr, 2)} m³/m²·h`, result: sharedFmt(hlr, 2), status: 'pass', unit: 'm³/m²·h' },
          { label: 'Peak HLR', formula: 'HLR_peak = q_peak / A', substitution: `HLR_peak = ${sharedFmt(peakQ, 1)} / ${sharedFmt(tankArea, 1)} = ${sharedFmt(peakHLR, 2)} m³/m²·h`, result: sharedFmt(peakHLR, 2), status: 'pass', unit: 'm³/m²·h' },
          { label: 'Volume turnover', formula: 'turnover = Q / V_w', substitution: `turnover = ${sharedFmt(Q, 0)} / ${sharedFmt(workingVolume, 1)} = ${sharedFmt(volumeTurnover, 1)} ×/day`, result: sharedFmt(volumeTurnover, 1), status: volumeTurnover <= 10 ? 'pass' : 'warn', unit: '×/day' },
          { label: 'Peak shave volume', formula: 'V_shave = (q_peak − q) × 2h', substitution: `V_shave = (${sharedFmt(peakQ, 1)} − ${sharedFmt(q, 1)}) × 2 = ${sharedFmt(peakShaveVolume, 1)} m³`, result: sharedFmt(peakShaveVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Peak shave %', formula: '% = V_shave / V_w × 100', substitution: `% = ${sharedFmt(peakShaveVolume, 1)} / ${sharedFmt(workingVolume, 1)} × 100 = ${sharedFmt(peakShavePercent, 1)}%`, result: sharedFmt(peakShavePercent, 1), status: peakShavePercent <= 50 ? 'pass' : 'warn', unit: '%' },
        ],
      },
      {
        title: 'Mixing Physics — Camp-Stein G-Value',
        icon: Zap,
        equations: [
          { label: 'Water viscosity', formula: 'μ = 0.001 × exp[−1.5×(T−20)/(T+273.15)]', substitution: `μ = 0.001 × exp[−1.5×${params.temperature - 20}/${sharedFmt(tempK, 1)}] = ${sharedFmt(mu * 1000, 3)} mPa·s`, result: sharedFmt(mu * 1000, 3), status: 'pass', unit: 'mPa·s' },
          { label: 'Mixer power', formula: 'P = mixerPower × 1000', substitution: `P = ${params.mixerPower} × 1000 = ${sharedFmt(mixerPowerW, 0)} W`, result: sharedFmt(mixerPowerW, 0), status: 'pass', unit: 'W' },
          { label: 'G-value', formula: 'G = √(P / μV)', substitution: `G = √(${sharedFmt(mixerPowerW, 0)} / (${sharedFmt(mu, 5)} × ${sharedFmt(workingVolume, 1)})) = ${sharedFmt(gValue, 0)}`, result: sharedFmt(gValue, 0), status: params.mixerType === 'none' ? 'fail' : gValue >= 15 ? 'pass' : 'warn', unit: 's⁻¹' },
          { label: 'Mixing time', formula: 't_mix = (V_w / q) × 60', substitution: `t_mix = (${sharedFmt(workingVolume, 1)} / ${sharedFmt(q, 1)}) × 60 = ${sharedFmt(mixingTime, 0)} min`, result: sharedFmt(mixingTime, 0), status: 'pass', unit: 'min' },
          { label: 'Turnover time', formula: 't_turn = V_w / q', substitution: `t_turn = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(q, 1)} = ${sharedFmt(turnoverTime, 1)} h`, result: sharedFmt(turnoverTime, 1), status: 'pass', unit: 'h' },
        ],
      },
      {
        title: 'Mass Loading & Volume Turnover',
        icon: BarChart3,
        equations: [
          { label: 'COD load', formula: 'L_COD = Q × COD / 1000', substitution: `L_COD = ${sharedFmt(Q, 0)} × ${params.influentCOD} / 1000 = ${sharedFmt(codLoad, 1)} kg/d`, result: sharedFmt(codLoad, 1), status: 'pass', unit: 'kg/d' },
          { label: 'BOD load', formula: 'L_BOD = Q × BOD / 1000', substitution: `L_BOD = ${sharedFmt(Q, 0)} × ${params.influentBOD} / 1000 = ${sharedFmt(bodLoad, 1)} kg/d`, result: sharedFmt(bodLoad, 1), status: 'pass', unit: 'kg/d' },
          { label: 'TSS load', formula: 'L_TSS = Q × TSS / 1000', substitution: `L_TSS = ${sharedFmt(Q, 0)} × ${params.influentTSS} / 1000 = ${sharedFmt(tssLoad, 1)} kg/d`, result: sharedFmt(tssLoad, 1), status: 'pass', unit: 'kg/d' },
          { label: 'Freeboard volume', formula: 'V_fb = V_total − V_w', substitution: `V_fb = ${sharedFmt(totalVolume, 1)} − ${sharedFmt(workingVolume, 1)} = ${sharedFmt(totalVolume - workingVolume, 1)} m³`, result: sharedFmt(totalVolume - workingVolume, 1), status: 'pass', unit: 'm³' },
        ],
      },
    ];
  }, [params]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    undersized: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    overloaded: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: ShieldAlert },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Waves}
        iconColor="text-emerald-400"
        title="Equalization Tank Specification"
        subtitle="Peak shaving, mixing physics, and detention time calculations for flow balancing"
        actions={<div className="flex items-center gap-2">
          <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
          <button onClick={() => navigate('/equipment-validation?tab=eq')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
        </div>}
      />

      <div className={`rounded-xl border p-4 ${sc.bg} ${sc.border}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sc.bg}`}><StatusIcon className={`w-5 h-5 ${sc.text}`} /></div>
          <div><p className={`text-sm font-bold ${sc.text}`}>{size.status.toUpperCase()}</p><p className={`text-xs ${sc.text} opacity-80`}>{size.statusDetail}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PROFILES.map((profile, i) => (
          <PC key={i} profile={profile} isActive={profileIndex === i} onClick={() => { setParams(profile.defaults); setProfileIndex(i); toast.info(`Loaded ${profile.name} profile`); }} />
        ))}
      </div>

      <SampleSelector loader={sampleLoader} />

      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'BOD', value: `${sharedFmt(params.influentBOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.bod || 0, 0)}` },
              { label: 'COD', value: `${sharedFmt(params.influentCOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.cod || 0, 0)}` },
              { label: 'TSS', value: `${sharedFmt(params.influentTSS, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tss || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-emerald-400" />Process Flow</h3>
            <button onClick={() => setShowFlow(!showFlow)} className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1"><Settings className="w-3 h-3" />{showFlow ? 'Collapse' : 'Expand'}</button>
          </div>
          <AnimatePresence>{showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}</AnimatePresence>
        </div>

        <div className="xl:col-span-5 space-y-4">
          <div className="p-4 rb-card">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-400" />Performance Gauges</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <G value={size.avgDetention} max={24} label="Avg Detention" unit="h" color="#10b981" />
              <G value={size.peakDetention} max={12} label="Peak Detention" unit="h" color="#06b6d4" />
              <G value={size.volumeTurnover} max={12} label="Turnover" unit="×/d" color="#f59e0b" />
              <G value={size.gValue} max={100} label="G-Value" unit="s⁻¹" color="#ef4444" />
              <G value={size.peakShavePercent} max={100} label="Peak Shave" unit="%" color="#a855f7" />
              <G value={size.codLoad} max={500} label="COD Load" unit="kg/d" color="#f97316" />
              <G value={size.hlr} max={5} label="HLR" unit="m³/m²·h" color="#22c55e" />
              <G value={size.mixerPower || 0} max={10} label="Mixer" unit="kW" color="#8b5cf6" />
            </div>
          </div>

          <div className="overflow-hidden rb-card">
            <div className="flex border-b border-slate-800">
              {(['influent', 'tank', 'mixing'] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${activeTab === tab ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/5' : 'text-slate-500 hover:text-slate-300'}`}>
                  {tab === 'influent' ? 'Influent' : tab === 'tank' ? 'Tank Design' : 'Mixing'}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-4">
              {activeTab === 'influent' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Flow Rate" value={params.flowRate} min={24} max={2400} step={12} unit="m³/day" icon={Waves} color="#06b6d4" onChange={(v: number) => setParams({ ...params, flowRate: v })} />
                  <S label="Peak Factor" value={params.peakFactor} min={1.2} max={5} step={0.1} unit="×" icon={ArrowRightLeft} color="#f59e0b" onChange={(v: number) => setParams({ ...params, peakFactor: v })} />
                  <S label="Min Flow" value={params.minFlow} min={10} max={500} step={5} unit="m³/day" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, minFlow: v })} />
                  <S label="Influent BOD" value={params.influentBOD} min={100} max={5000} step={50} unit="mg/L" icon={FlaskConical} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentBOD: v })} />
                  <S label="Influent COD" value={params.influentCOD} min={200} max={8000} step={100} unit="mg/L" icon={Beaker} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentCOD: v })} />
                  <S label="Influent TSS" value={params.influentTSS} min={50} max={2000} step={50} unit="mg/L" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentTSS: v })} />
                  <S label="Influent pH" value={params.influentpH} min={4} max={10} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={(v: number) => setParams({ ...params, influentpH: v })} />
                  <S label="Temperature" value={params.temperature} min={5} max={35} step={1} unit="°C" icon={Thermometer} color="#ef4444" onChange={(v: number) => setParams({ ...params, temperature: v })} />
                  <S label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v: number) => setParams({ ...params, og: v })} />
                  <S label="Influent pH (sample)" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={FlaskConical} color="#a855f7" onChange={(v: number) => setParams({ ...params, ph: v })} />
                  <S label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={Beaker} color="#10b981" onChange={(v: number) => setParams({ ...params, alkalinity: v })} />
                  <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, turbidity: v })} />
                  <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v: number) => setParams({ ...params, conductivity: v })} />
                  <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v: number) => setParams({ ...params, hardness: v })} />
                  <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, nh3n: v })} />
                  <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v: number) => setParams({ ...params, tp: v })} />
                </div>
              )}
              {activeTab === 'tank' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Tank Length" value={params.tankLength} min={3} max={20} step={0.5} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankLength: v })} />
                  <S label="Tank Width" value={params.tankWidth} min={2} max={12} step={0.5} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankWidth: v })} />
                  <S label="Tank Depth" value={params.tankDepth} min={2} max={6} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankDepth: v })} />
                  <S label="Freeboard" value={params.freeboard} min={0.3} max={1.5} step={0.1} unit="m" icon={Volume2} color="#06b6d4" onChange={(v: number) => setParams({ ...params, freeboard: v })} />
                  <S label="Working Vol %" value={params.workingVolumePercent} min={70} max={95} step={5} unit="%" icon={Gauge} color="#06b6d4" onChange={(v: number) => setParams({ ...params, workingVolumePercent: v })} />
                </div>
              )}
              {activeTab === 'mixing' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Mixer Type</p>
                    <div className="flex gap-2 flex-wrap">
                      {(['none', 'jet', 'mechanical', 'diffused'] as const).map((t) => (
                        <button key={t} onClick={() => setParams({ ...params, mixerType: t })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${params.mixerType === t ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <S label="Mixer Power" value={params.mixerPower} min={0.5} max={10} step={0.1} unit="kW" icon={Zap} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, mixerPower: v })} />
                  <S label="Target Retention" value={params.retentionTime} min={2} max={24} step={0.5} unit="h" icon={Timer} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, retentionTime: v })} />
                </div>
              )}
            </div>
          </div>

          <div className="p-4 space-y-4 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-400" />Results & Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Hydraulics</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Avg detention</span><span className="text-white font-bold">{fmt(size.avgDetention, 1)} h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Peak detention</span><span className="text-white font-bold">{fmt(size.peakDetention, 1)} h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Min detention</span><span className="text-white font-bold">{fmt(size.minDetention, 1)} h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Volume turnover</span><span className="text-emerald-400 font-bold">{fmt(size.volumeTurnover, 1)} ×/day</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Mass Loading</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">COD load</span><span className="text-amber-400 font-bold">{fmt(size.codLoad, 1)} kg/d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">BOD load</span><span className="text-amber-400 font-bold">{fmt(size.bodLoad, 1)} kg/d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">TSS load</span><span className="text-amber-400 font-bold">{fmt(size.tssLoad, 1)} kg/d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Peak shave</span><span className="text-emerald-400 font-bold">{fmt(size.peakShavePercent, 1)} %</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Mixing & Risks</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">G-value</span><span className="text-sky-400 font-bold">{fmt(size.gValue, 0)} s⁻¹</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Turnover</span><span className="text-sky-400 font-bold">{fmt(size.turnoverTime, 1)} h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Stratification</span><span className={`${size.stratificationRisk === 'high' ? 'text-rose-400' : size.stratificationRisk === 'moderate' ? 'text-amber-400' : 'text-emerald-400'} font-bold`}>{size.stratificationRisk}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Odour risk</span><span className={`${size.odourRisk === 'high' ? 'text-rose-400' : size.odourRisk === 'moderate' ? 'text-amber-400' : 'text-emerald-400'} font-bold`}>{size.odourRisk}</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes & Warnings</h3>
            <div className="space-y-2">
              {size.designNotes.map((note, i) => (
                <div key={i} className={`text-xs p-2 rounded-lg ${note.startsWith('⚠️') ? 'bg-rose-500/5 border border-rose-500/20 text-rose-400' : note.startsWith('ℹ️') ? 'bg-sky-500/5 border border-sky-500/20 text-sky-400' : 'bg-emerald-500/5 border border-emerald-500/20 text-emerald-400'}`}>{note}</div>
              ))}
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
